import React from 'react';
import { Box } from '@mui/material';
import ExpiryRing from '../../../components/ExpiryRing';
import { MillosMark } from '../icons';
import {
  homepageMockups as M,
  marketingColors as C,
  marketingFonts as F,
  visualTokens as V,
} from '../../../theme_landing_page_2';

const spending = [
  { month: 'Jan', amount: 420 },
  { month: 'Feb', amount: 610 },
  { month: 'Mar', amount: 480 },
  { month: 'Apr', amount: 760 },
  { month: 'May', amount: 590 },
  { month: 'Jun', amount: 680 },
];
const points = spending.map(({ amount }, i) => `${34 + i * 44},${84 - amount / 800 * 66}`).join(' ');
const total = spending.reduce((sum, { amount }) => sum + amount, 0);
const pounds = (amount: number) => `£${amount.toLocaleString('en-GB')}`;
const previewCardSx = { ...M.panel, height: { xs: 280, md: 'clamp(250px, calc(100svh - 510px), 320px)' }, boxSizing: 'border-box', display: 'flex', flexDirection: 'column', padding: '16px', position: 'relative', zIndex: 1,
  '@media (min-width:640px) and (max-width:1100px)': { height: 'clamp(220px, calc(100svh - 450px), 280px)', padding: '10px', textAlign: 'left' },
};
const figureSx = { m: 0, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '16px' };
const outcomeTitleSx = { m: 0, fontFamily: F.ui, fontSize: V.fontSize16, fontWeight: V.fontWeight600, lineHeight: V.lineHeight1_4 };
const compact = '@media (max-width:639.95px)';
const foldable = '@media (min-width:640px) and (max-width:1100px)';
// Account for the portrait's 115.5% width while retaining 5.5% overlap of its visible width.
const foldableOverlap = -1.327625;

const Check = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="m5 12 4 4L19 6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const MillosProductPreview: React.FC = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dateAfter = (days: number) => {
    return new Date(today.getTime() + days * 86400000);
  };
  const expiryDate = dateAfter(150);
  const issueDate = dateAfter(150 - 365);
  return (
  <Box sx={{ position: 'relative', top: '-16px', width: '100%', maxWidth: 1120, margin: '64px auto 0', display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(0, 1.15fr) minmax(0, 1fr)' }, alignItems: 'center', columnGap: '16px', rowGap: '28px',
    [compact]: { display: 'block', height: 350, maxWidth: 430, mt: '44px', top: 0, '& figcaption': { display: 'none' } },
    [foldable]: { top: 0, maxWidth: 'min(1000px, calc((100svh - 340px) * 2.3072))', mt: 'auto', mb: 'auto', py: '12px', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 0.95fr) minmax(0, 1fr)', columnGap: 0, '& figcaption': { '& h3': { fontSize: 13 } } },
  }}>
    <Box component="figure" aria-label="Certificates product preview" sx={{ ...figureSx, [compact]: { position: 'absolute', left: 0, top: 8, width: '68%', maxWidth: 250, zIndex: 2 } }}>
    <Box sx={{ ...previewCardSx, mr: { md: '-28px' }, transform: { md: 'translateX(24px) rotate(-2deg)' }, [compact]: { height: 'auto', padding: '12px', textAlign: 'left', transform: 'rotate(-4deg)', mr: 0 }, [foldable]: { ...previewCardSx[foldable], mr: 0, transform: `translateX(${foldableOverlap}%)` } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', mb: '10px', [compact]: { display: 'none' } }}>
        <Box sx={{ fontFamily: F.ui, fontSize: V.fontSize14, fontWeight: V.fontWeight600 }}>Certificates</Box>
      </Box>
      <Box sx={{ background: C.panel, borderRadius: V.borderRadius12px, padding: '12px', border: `1px solid ${C.hairline}`, position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', [compact]: { background: 'transparent', p: 0, border: 0 } }}>
        <Box component="img" src="/marketing/gas-certificate-redacted.png" alt="Gas Safety certificate preview with identifying details redacted" loading="lazy" sx={{ width: '100%', flex: 1, minHeight: 0, objectFit: 'contain', display: 'block', border: `1px solid ${C.glassBorder}`, borderRadius: V.borderRadius6px, mb: '8px', [compact]: { display: 'none' } }} />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', [compact]: { gap: '8px' } }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <Box sx={{ fontFamily: F.ui, fontSize: V.fontSize18, fontWeight: V.fontWeight600, lineHeight: V.lineHeight1_4, [compact]: { display: 'none' }, [foldable]: { fontSize: 13 } }}>Gas Certificate</Box>
              <Box sx={{ display: { xs: 'block', md: 'none' }, fontFamily: F.ui, fontSize: 13, fontWeight: V.fontWeight600, [foldable]: { display: 'none' } }}>Gas certificate</Box>
            </Box>
            <Box sx={{ display: { xs: 'block', md: 'none' }, color: V.tealText, fontSize: 12, mt: 1, [foldable]: { display: 'none' } }}>AI renewal planned ✓</Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', color: C.muted, fontSize: V.fontSize12, mt: '6px', [compact]: { display: 'none' } }}>
              <MillosMark size={18} style={{ flexShrink: 0 }} />
              <Box component="span">Renewal starts in 120d</Box>
            </Box>
          </Box>
          <Box role="img" aria-label="150 days left until certificate expiry" sx={{
            flexShrink: 0,
            [compact]: {
              '& > div': { width: 50, height: 50 },
              '& circle': { strokeWidth: 5 },
              '& .MuiTypography-root:first-of-type': { transform: 'scale(0.9)' },
              '& .MuiTypography-root + .MuiTypography-root': { display: 'none' },
            },
          }}>
            <ExpiryRing issueDate={issueDate.toISOString()} expiryDate={expiryDate.toISOString()} size={56} />
          </Box>
        </Box>
        <Box sx={{ position: 'absolute', right: -12, top: -12, width: 44, height: 44, borderRadius: '50%', background: M.success, color: C.white, border: `4px solid ${C.white}`, display: 'grid', placeItems: 'center', [compact]: { display: 'none' } }}><Check size={25} /></Box>
      </Box>
    </Box>
      <Box component="figcaption">
        <Box component="h3" sx={outcomeTitleSx}>Stay ahead of renewals.</Box>
      </Box>
    </Box>

    <Box sx={{ width: '90%', maxWidth: { xs: 189, md: 'min(378px, calc((100svh - 340px) * 0.6687))' }, aspectRatio: '752 / 1012', transform: 'scale(0.9426375)', justifySelf: 'center', order: { xs: -1, md: 0 },
      [compact]: { position: 'absolute', width: '58%', maxWidth: 240, left: '50%', top: '50%', transform: 'translate(-50%, -50%)' },
      [foldable]: { width: '115.5%', maxWidth: 'none', transform: 'none', order: 0 },
    }}>
      <Box component="img" loading="lazy" src="/marketing/front_guy.avif" alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: V.borderRadius32px, display: 'block' }} />
    </Box>

    <Box component="figure" aria-label="Accounting product preview" sx={{ ...figureSx, [compact]: { position: 'absolute', right: -8, bottom: -4, width: '47%', maxWidth: 180, zIndex: 2 } }}>
    <Box sx={{ ...previewCardSx, ml: { md: '-28px' }, transform: { md: 'translateX(-24px) rotate(2deg)' }, [compact]: { height: 'auto', padding: '12px', textAlign: 'left', transform: 'rotate(4deg)', ml: 0 }, [foldable]: { ...previewCardSx[foldable], ml: 0, transform: `translateX(${-foldableOverlap}%)` } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', mb: '10px' }}>
        <Box sx={{ fontFamily: F.ui, fontSize: V.fontSize14, fontWeight: V.fontWeight600 }}>Accounting</Box>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
        <Box sx={{ color: C.muted, fontSize: V.fontSize12, [compact]: { display: 'none' } }}>Spending</Box>
        <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'space-between', alignItems: 'baseline', gap: '8px', width: '100%', fontFamily: F.display, fontSize: 16, color: C.body, [foldable]: { display: 'none' } }}>
          <Box component="span">Expenses</Box>
          <Box component="span" sx={{ fontWeight: V.fontWeight700, whiteSpace: 'nowrap' }}>{pounds(total)}</Box>
        </Box>
        <Box sx={{ fontFamily: F.display, fontSize: V.fontSize34, lineHeight: V.lineHeight1_2, [compact]: { display: 'none' } }}>{pounds(total)}</Box>
      </Box>
      <Box sx={{ color: C.muted, fontSize: V.fontSize11, mt: '4px', [compact]: { display: 'none' } }}>Maintenance</Box>
      <Box component="svg" role="img" aria-label={`Example monthly spending: ${spending.map(({ month, amount }) => `${month} ${pounds(amount)}`).join(', ')}.`} viewBox="0 0 280 110" sx={{ display: 'block', width: '100%', flex: 1, minHeight: 0, mt: '8px', overflow: 'visible', [compact]: { flex: 'none', '& text, & line': { display: 'none' } } }}>
        {[0, 400, 800].map((amount) => {
          const y = 84 - amount / 800 * 66;
          return (
            <g key={amount}>
              <line x1="34" y1={y} x2="254" y2={y} stroke={M.gridLine} strokeDasharray="3 5" />
              <text x="26" y={y + 3} textAnchor="end" fill={C.muted} fontFamily={F.body} fontSize={V.fontSize10}>{amount}</text>
            </g>
          );
        })}
        <polygon points={`34,84 ${points} 254,84`} fill={M.chartFill} />
        <polyline points={points} stroke={M.chartLine} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" fill="none" />
        {spending.map(({ month, amount }, i) => (
          <g key={month}>
            <circle cx={34 + i * 44} cy={84 - amount / 800 * 66} r="3.5" fill={C.white} stroke={M.chartLine} strokeWidth="2" />
            <text x={34 + i * 44} y="104" textAnchor="middle" fill={C.muted} fontFamily={F.body} fontSize={V.fontSize10}>{month}</text>
          </g>
        ))}
      </Box>
      <Box sx={{ borderTop: `1px solid ${C.hairline}`, pt: '8px', mt: '8px', display: 'flex', justifyContent: 'space-between', gap: '10px', fontSize: V.fontSize12, [compact]: { display: 'none' } }}>
        <Box sx={{ color: C.muted }}>Latest expense</Box>
        <Box sx={{ fontWeight: V.fontWeight600 }}>Gas Safety · £85</Box>
      </Box>
    </Box>
      <Box component="figcaption">
        <Box component="h3" sx={outcomeTitleSx}>Keep your finances organised.</Box>
      </Box>
    </Box>
  </Box>
  );
};

export default MillosProductPreview;
